import React, {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Divider,
  IconButton,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import { CustomModal } from "@/components/Modal";
import { HttpStatusCode, isAxiosError } from "axios";
import type { GetPropertySummaryDetailAPIResponse } from "@/features/propertySummary/types";
import { toast } from "react-toastify";
import { useApiClient } from "@/hooks/useApiClient";
import { GetPropertySummaryDetailAPI } from "@/features/propertySummary/api";
import EditPropertySummary from "@/features/propertySummary/api/EditPropertySummaryAPI";
import Grid from "@mui/material/Grid";
import { CTextField } from "@/components/TextField/CTextField";
import { Controller, type Path, useForm, useWatch } from "react-hook-form";
import {
  type PropertySummaryEditForm,
  PropertySummaryEditSchema,
} from "@/features/propertySummary/types/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { SizingWrapperStyle } from "@/components/Wrapper";
import {
  Button,
  ButtonColorOption,
  ButtonVariantOption,
} from "@/components/Button";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker } from "@/components/DatePicker";
import ModifyMap, {
  type ModifyMapHandle,
} from "@/features/propertySummary/components/ModifyMap";
import { LatLng } from "leaflet";
import { CTextFieldPrice } from "@/components/TextField/CTextFieldPrice";
import { SendToTrackingService } from "@/api/handlers/errors";

export const FieldLabel: React.FC<{ children: React.ReactNode }> = ({
  children,
  ...rest
}) => {
  return (
    <Typography variant="subtitle2" component="div" mt={2} mb={1} {...rest}>
      {children}
    </Typography>
  );
};

export interface EditModalProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  handleClose: (
    event: Record<string, unknown>,
    reason: "backdropClick" | "escapeKeyDown"
  ) => void;
  children?: React.ReactNode;
  sheetId: number | null;
}

export interface LocalPreview {
  file: File | null;
  url: string;
}

export const EditModal: React.FC<EditModalProps> = ({
  open,
  setOpen,
  handleClose,
  sheetId,
}) => {
  const { apiClient } = useApiClient();
  const saveProperty = EditPropertySummary();

  const {
    control,
    setValue,
    getValues,
    reset,
    resetField,
    handleSubmit,
    watch,
  } = useForm<PropertySummaryEditForm>({
    mode: "all",
    defaultValues: {
      buildingName: "",
      price: "",
      address: "",
      chiban: "",
      lat: null,
      lng: null,
      kaokuNumbersDisplay: "",
      access: "",
      landArea: "",
      landCategory: "",
      areaUsePurpose: "",
      ordinance: "",
      buildingCoverageRatio: "",
      volumeRate: "",
      buildingVariant: "",
      buildingType: "",
      area: "",
      buildDate: null,
      balconyArea: "",
      buildingCount: "",
      yield: "",
      remarks: "",
      description: "",
      mapPhotos: [],
      sitePhoto_1: {
        id: null,
        file: null,
        url: null,
      },
      sitePhoto_2: {
        id: null,
        file: null,
        url: null,
      },
    },
    resolver: zodResolver(PropertySummaryEditSchema),
  });

  // 前回表示していた sheetId を保持しておき、同じ物件かどうか判定する
  const prevSheetIdRef = useRef<number | null>(null);

  const { data: apiResponse, error } = GetPropertySummaryDetailAPI(apiClient, {
    id: sheetId,
  });

  // 固定2枠のスロット（index 0, 1）。未選択は null
  const [slots, setSlots] = useState<Array<LocalPreview | null>>([null, null]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadMessage, setUploadMessage] = useState<string>("");

  // 地図編集関連
  const [mapEditMode, setMapEditMode] = useState<boolean>(false);
  const [mapSlot, setMapSlot] = useState<LocalPreview | null>(null);

  // モーダルが閉じられたときにprevSheetIdRefをリセット
  useEffect(() => {
    if (!open) {
      prevSheetIdRef.current = null;
      // モーダルが閉じられたときにmapSlotもリセット
      if (mapSlot?.file) {
        URL.revokeObjectURL(mapSlot.url);
      }
      setMapSlot(null);
      setMapEditMode(false);
    }
  }, [open, mapSlot]);

  useEffect(() => {
    if (!apiResponse || sheetId == null) return;

    // 同じ sheetId の場合は初期化しない（編集途中の状態を保持する）
    if (prevSheetIdRef.current === sheetId) return;

    reset();

    // 基本情報
    setValue("buildingName", apiResponse.name ?? "");
    setValue(
      "price",
      apiResponse.price != null ? String(apiResponse.price) : ""
    );

    setValue("lat", apiResponse.latitude);
    setValue("lng", apiResponse.longitude);

    // 所在地
    setValue("address", apiResponse.address ?? "");
    setValue("chiban", apiResponse.chiban ?? "");
    setValue("kaokuNumbersDisplay", apiResponse.kaokuNumbersDisplay ?? "");

    // 交通
    setValue("access", apiResponse.access ?? "");

    // 土地
    setValue(
      "landArea",
      apiResponse.landArea != null ? String(apiResponse.landArea) : ""
    );
    setValue(
      "landCategory",
      apiResponse.landCategory != null ? String(apiResponse.landCategory) : ""
    );
    setValue(
      "areaUsePurpose",
      apiResponse.areaUsePurpose != null
        ? String(apiResponse.areaUsePurpose)
        : ""
    );
    setValue(
      "ordinance",
      apiResponse.ordinance != null ? String(apiResponse.ordinance) : ""
    );
    setValue(
      "buildingCoverageRatio",
      apiResponse.buildingRate != null ? String(apiResponse.buildingRate) : ""
    );
    setValue(
      "volumeRate",
      apiResponse.volumeRate != null ? String(apiResponse.volumeRate) : ""
    );

    // 建物
    setValue(
      "buildingVariant",
      apiResponse.buildingStructure != null
        ? String(apiResponse.buildingStructure)
        : ""
    );
    setValue(
      "buildingType",
      apiResponse.buildingType != null ? String(apiResponse.buildingType) : ""
    );
    setValue(
      "area",
      apiResponse.buildingTotalFloorAreaExclusive != null
        ? String(apiResponse.buildingTotalFloorAreaExclusive)
        : ""
    );
    setValue(
      "buildDate",
      apiResponse.buildingNewConstructionDate != null
        ? new Date(apiResponse.buildingNewConstructionDate)
        : null
    );
    setValue(
      "balconyArea",
      apiResponse.balconyArea != null ? String(apiResponse.balconyArea) : ""
    );
    setValue(
      "buildingCount",
      apiResponse.unitsAndLayout != null
        ? String(apiResponse.unitsAndLayout)
        : ""
    );

    // 利回り
    setValue(
      "yield",
      apiResponse.yieldRate != null ? String(apiResponse.yieldRate) : ""
    );

    // 備考
    setValue(
      "remarks",
      apiResponse.remarks != null ? String(apiResponse.remarks) : ""
    );

    setValue("description", apiResponse.description);

    // 地図写真
    if (apiResponse.mapPhotos.length > 0) {
      const photos = apiResponse.mapPhotos.map((value) => {
        return {
          id: value.id,
          display_order: value.displayOrder,
          url: value.uri,
          file: null,
        };
      });
      setValue("mapPhotos", photos);
    } else {
      setValue("mapPhotos", []);
    }
    // 現況写真（フォーム値と slots の両方を初期化）
    const initialSlots: Array<LocalPreview | null> = [null, null];

    if (apiResponse.sitePhotos && apiResponse.sitePhotos.length > 0) {
      const sitePhotoKeys: [
        Path<PropertySummaryEditForm>,
        Path<PropertySummaryEditForm>
      ] = ["sitePhoto_1", "sitePhoto_2"];

      const photos = apiResponse.sitePhotos;

      photos.forEach((value) => {
        if (value.displayOrder > 2) return;
        const displayOrder = value.displayOrder - 1;

        initialSlots[displayOrder] = { file: null, url: value.uri };

        // RHFにセット
        setValue(sitePhotoKeys[displayOrder], {
          id: value.id,
          file: null,
          url: value.uri,
        });
      });
    } else {
      resetField("sitePhoto_1");
      resetField("sitePhoto_2");
    }

    setSlots((prev) => {
      prev.forEach((slot) => {
        // file を持っているものは createObjectURL で作ったローカルURLとみなして破棄
        if (slot?.file) {
          URL.revokeObjectURL(slot.url);
        }
      });
      return initialSlots;
    });
    // 地図関係のリセット
    setMapEditMode(false);
    setMapSlot(null);

    // 最後に今回扱った sheetId を記録
    prevSheetIdRef.current = sheetId;
  }, [apiResponse, sheetId, reset, setValue, resetField]);

  if (
    isAxiosError<GetPropertySummaryDetailAPIResponse>(error) &&
    (error.response?.status === HttpStatusCode.BadRequest ||
      error.response?.status === HttpStatusCode.NotFound)
  ) {
    toast.error("指定のデータが存在しません。");
  }
  if (
    isAxiosError<GetPropertySummaryDetailAPIResponse>(error) &&
    (error.response?.status ?? HttpStatusCode.InternalServerError) >=
      HttpStatusCode.InternalServerError
  ) {
    toast.error(
      "データの取得に失敗しました。しばらく経ってから再度お試しください。"
    );
  }

  const watchedMapPhotos = useWatch({ control, name: "mapPhotos" });
  const mapPhotoUrl = watchedMapPhotos?.[0]?.url ?? null;

  const [lat, lng] = watch(["lat", "lng"]);
  const latlng = lat && lng ? new LatLng(lat, lng) : null;
  const modifyMapRef = useRef<ModifyMapHandle>(null);

  useEffect(() => {
    if (mapSlot?.url) {
      const photo = {
        id: null,
        display_order: 0,
        url: mapSlot.url,
        file: mapSlot.file,
      };
      setValue("mapPhotos", [photo]);
    } else if (mapSlot === null) {
      // mapSlotがnullの場合は、mapSlotから設定されたmapPhotosをクリアする
      // APIから取得したデータ（fileがnull）は保持する
      const currentMapPhotos = getValues("mapPhotos");
      const apiPhotos = currentMapPhotos.filter((photo) => photo.file === null);
      setValue("mapPhotos", apiPhotos);
    }
  }, [mapSlot, setValue, getValues]);

  return (
    <CustomModal
      isOpen={open}
      handleClose={handleClose}
      ariaLabelledby={"edit-modal-title"}
      ariaDescribedby={"edit-modal-description"}
      overflow="auto"
      width="80%"
      maxHeight="80vh"
    >
      <Typography id="property-edit-modal-title" variant="h6" component="h2">
        物件概要書編集
      </Typography>
      <Box id="property-edit-modal-description" sx={{ my: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={7}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant={"h6"}>基本情報</Typography>
                    <FieldLabel>物件名</FieldLabel>
                    <CTextField
                      label={"物件名"}
                      control={control}
                      name={"buildingName"}
                      size={SizingWrapperStyle.INHERIT}
                    />
                    <FieldLabel>価格</FieldLabel>
                    <CTextFieldPrice
                      label={"価格"}
                      control={control}
                      name={"price"}
                      size={SizingWrapperStyle.INHERIT}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant={"h6"}>所在地</Typography>
                    <FieldLabel>住居表示</FieldLabel>
                    <CTextField
                      label={"住居表示"}
                      control={control}
                      name={"address"}
                      size={SizingWrapperStyle.INHERIT}
                      disabled
                    />
                    <FieldLabel>地番</FieldLabel>
                    <CTextField
                      label={"地番"}
                      control={control}
                      name={"chiban"}
                      size={SizingWrapperStyle.INHERIT}
                      disabled
                    />
                    <FieldLabel>家屋番号</FieldLabel>
                    <CTextField
                      label={"家屋番号"}
                      control={control}
                      name={"kaokuNumbersDisplay"}
                      size={SizingWrapperStyle.INHERIT}
                      disabled
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant={"h6"}>交通</Typography>
                    <FieldLabel>最寄駅</FieldLabel>
                    <CTextField
                      multiline
                      rows={3}
                      label={"最寄駅、鉄道路線、徒歩時間等を入力"}
                      control={control}
                      name={"access"}
                      size={SizingWrapperStyle.INHERIT}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant={"h6"}>土地</Typography>
                    <FieldLabel>地積(m²)</FieldLabel>
                    <CTextField
                      label={"地積"}
                      control={control}
                      name={"landArea"}
                      size={SizingWrapperStyle.INHERIT}
                    />
                    <FieldLabel>地目</FieldLabel>
                    <CTextField
                      label={"地目"}
                      control={control}
                      name={"landCategory"}
                      size={SizingWrapperStyle.INHERIT}
                    />
                    <FieldLabel>用途地域</FieldLabel>
                    <CTextField
                      label={"用途地域"}
                      control={control}
                      name={"areaUsePurpose"}
                      size={SizingWrapperStyle.INHERIT}
                    />
                    <FieldLabel>条例等</FieldLabel>
                    <CTextField
                      label={"条例等"}
                      control={control}
                      name={"ordinance"}
                      size={SizingWrapperStyle.INHERIT}
                    />
                    <FieldLabel>建蔽率(%)</FieldLabel>
                    <CTextField
                      label={"建蔽率"}
                      control={control}
                      name={"buildingCoverageRatio"}
                      size={SizingWrapperStyle.INHERIT}
                    />
                    <FieldLabel>容積率(%)</FieldLabel>
                    <CTextField
                      label={"容積率"}
                      control={control}
                      name={"volumeRate"}
                      size={SizingWrapperStyle.INHERIT}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant={"h6"}>建物</Typography>
                    <FieldLabel>構造・規模</FieldLabel>
                    <CTextField
                      label={"構造・規模"}
                      control={control}
                      name={"buildingVariant"}
                      size={SizingWrapperStyle.INHERIT}
                    />
                    <FieldLabel>種類</FieldLabel>
                    <CTextField
                      label={"種類"}
                      control={control}
                      name={"buildingType"}
                      size={SizingWrapperStyle.INHERIT}
                    />
                    <FieldLabel>専有面積(m²)</FieldLabel>
                    <CTextField
                      label={"専有面積"}
                      control={control}
                      name={"area"}
                      size={SizingWrapperStyle.INHERIT}
                    />
                    <FieldLabel>築年月日</FieldLabel>
                    <Controller
                      control={control}
                      name={"buildDate"}
                      render={({
                        field: { onChange, value, disabled },
                        fieldState,
                      }) => (
                        <DatePicker
                          label={"築年月日"}
                          value={value}
                          views={["year", "month", "day"]}
                          onChange={onChange}
                          inputFormat={"yyyy/MM/dd"}
                          disabled={disabled}
                          displayLabel={true}
                          error={fieldState.error?.message}
                        />
                      )}
                    />
                    <FieldLabel>バルコニー面積(m²)</FieldLabel>
                    <CTextField
                      label={"バルコニー面積"}
                      control={control}
                      name={"balconyArea"}
                      size={SizingWrapperStyle.INHERIT}
                    />
                    <FieldLabel>戸数・間取り</FieldLabel>
                    <CTextField
                      label={"戸数・間取り"}
                      control={control}
                      name={"buildingCount"}
                      size={SizingWrapperStyle.INHERIT}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant={"h6"}>利回り</Typography>
                    <FieldLabel>利回り</FieldLabel>
                    <CTextField
                      label={"利回り"}
                      control={control}
                      name={"yield"}
                      size={SizingWrapperStyle.INHERIT}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant={"h6"}>備考</Typography>
                    <FieldLabel>備考</FieldLabel>
                    <CTextField
                      multiline
                      rows={3}
                      label={"備考"}
                      control={control}
                      name={"remarks"}
                      size={SizingWrapperStyle.INHERIT}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={5}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant={"h6"}>地図</Typography>
                    <Box
                      height={"300px"}
                      border={1}
                      textAlign={"center"}
                      sx={{
                        position: "relative",
                        borderRadius: 1,
                        overflow: "hidden",
                      }}
                    >
                      {mapPhotoUrl ? (
                        <img
                          src={mapPhotoUrl}
                          alt="地図の画像"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            display: "block",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#666",
                          }}
                        >
                          地図の画像
                        </Box>
                      )}
                    </Box>
                    <Box mt={1}>
                      {!mapEditMode && (
                        <Button
                          variant={ButtonVariantOption.Outlined}
                          label={"地図を編集する"}
                          size={SizingWrapperStyle.SMALL}
                          onClick={() => {
                            setMapEditMode(true);
                          }}
                        />
                      )}
                      {mapEditMode && (
                        <>
                          <ModifyMap
                            ref={modifyMapRef}
                            initialPosition={latlng}
                            zoom={16}
                            isEditable={false}
                            width="300px"
                            height="300px"
                          />
                          <Stack direction="row" spacing={1}>
                            <Button
                              label={"確定する"}
                              onClick={() => {
                                (async () => {
                                  if (modifyMapRef.current) {
                                    const file =
                                      await modifyMapRef.current.genImage();
                                    if (file) {
                                      setMapSlot((prev) => {
                                        if (prev) {
                                          if (prev.file)
                                            URL.revokeObjectURL(prev.url);
                                        }
                                        return {
                                          file,
                                          url: URL.createObjectURL(file),
                                        };
                                      });
                                      setMapEditMode(false);
                                    }
                                  }
                                })();
                              }}
                              size={SizingWrapperStyle.SMALL}
                              variant={ButtonVariantOption.Outlined}
                            />
                            <Button
                              label={"変更を中止する"}
                              onClick={() => {
                                setMapEditMode(false);
                              }}
                              size={SizingWrapperStyle.SMALL}
                              color={ButtonColorOption.Error}
                              variant={ButtonVariantOption.Outlined}
                            />
                          </Stack>
                        </>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant={"h6"}>自由記述</Typography>
                    <Typography
                      variant={"body2"}
                      color={"text.secondary"}
                      sx={{ mb: 1 }}
                    >
                      地図画像と現況写真の間に表示される文言です
                    </Typography>
                    <CTextField
                      multiline
                      rows={3}
                      name={"description"}
                      control={control}
                      size={SizingWrapperStyle.INHERIT}
                      label={"自由入力"}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant={"h6"}>現況写真</Typography>
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={2}>
                        {[0, 1].map((idx) => {
                          const slot = slots[idx];
                          return (
                            <Box
                              key={idx}
                              sx={{
                                flex: 1,
                                height: "140px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                position: "relative",
                                cursor: "pointer",
                              }}
                            >
                              {!slot ? (
                                <Box
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    borderRadius: 2,
                                    border: "2px dashed",
                                    borderColor: "#bbb",
                                  }}
                                >
                                  <label
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <input
                                      type="file"
                                      accept="image/*"
                                      style={{ display: "none" }}
                                      onChange={(
                                        e: React.ChangeEvent<HTMLInputElement>
                                      ) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;

                                        if (!file.type.startsWith("image/")) {
                                          setUploadMessage(
                                            "画像ファイルを選択してください"
                                          );
                                          setUploadOpen(true);
                                          return;
                                        }

                                        const maxSize = 1 * 1024 * 1024;
                                        if (file.size > maxSize) {
                                          setUploadMessage(
                                            "ファイルサイズは1MB以下にしてください"
                                          );
                                          setUploadOpen(true);
                                          return;
                                        }

                                        // 画像をRHFに代入
                                        const sitePhotos = getValues([
                                          "sitePhoto_1",
                                          "sitePhoto_2",
                                        ]);

                                        sitePhotos[idx].file = file;

                                        setValue("sitePhoto_1", sitePhotos[0]);
                                        setValue("sitePhoto_2", sitePhotos[1]);

                                        // UI上にアップロード予定画像を表示する
                                        const url = URL.createObjectURL(file);
                                        setSlots((prev) => {
                                          const next = [...prev];
                                          const old = next[idx];
                                          if (old) URL.revokeObjectURL(old.url);
                                          next[idx] = { file, url };
                                          return next;
                                        });
                                      }}
                                    />
                                    <Typography color="text.secondary">
                                      クリックして画像を設定
                                    </Typography>
                                  </label>
                                </Box>
                              ) : (
                                <>
                                  <img
                                    src={slot.url}
                                    alt={`upload-${idx}`}
                                    style={{
                                      objectFit: "cover",
                                      width: "100%",
                                      height: "100%",
                                    }}
                                  />
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setSlots((prev) => {
                                        const sitePhotoKeys: [
                                          Path<PropertySummaryEditForm>,
                                          Path<PropertySummaryEditForm>
                                        ] = ["sitePhoto_1", "sitePhoto_2"];
                                        const next = [...prev];
                                        const old = next[idx];
                                        if (old) URL.revokeObjectURL(old.url);
                                        next[idx] = null;

                                        // RHFから保存されている画像を削除
                                        setValue(sitePhotoKeys[idx], {
                                          id: null,
                                          file: null,
                                          url: null,
                                        });
                                        return next;
                                      });
                                    }}
                                    sx={{
                                      position: "absolute",
                                      top: 4,
                                      right: 4,
                                      backgroundColor: "rgba(0,0,0,0.4)",
                                      color: "#FFF",
                                      "&:hover": {
                                        backgroundColor: "rgba(0,0,0,0.4)",
                                        color: "#BBB",
                                      },
                                    }}
                                    aria-label="remove-image"
                                  >
                                    <CloseIcon />
                                  </IconButton>
                                </>
                              )}
                            </Box>
                          );
                        })}
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <Divider />
      <Stack
        id={"property-edit-modal-action"}
        direction={"row"}
        spacing={2}
        justifyContent={"flex-end"}
        mt={2}
      >
        <Button
          label={"キャンセル"}
          onClick={() => {
            setOpen(false);
          }}
          variant={ButtonVariantOption.Outlined}
          size={SizingWrapperStyle.SMALL}
        />
        {/* <Button */}
        {/*  label={"プレビュー"} */}
        {/*  onClick={() => { */}
        {/*    setOpen(false); */}
        {/*  }} */}
        {/*  variant={ButtonVariantOption.Outlined} */}
        {/*  size={SizingWrapperStyle.SMALL} */}
        {/* /> */}
        <Button
          label={"保存"}
          onClick={() => {
            void handleSubmit(
              async (values) => {
                if (sheetId == null) {
                  toast.error("対象の物件IDが取得できません");
                  return;
                }
                try {
                  await saveProperty({ apiClient, sheetId, values });
                  toast.success("保存しました");
                  setOpen(false);
                } catch (e: unknown) {
                  console.error(e);
                  toast.error("保存に失敗しました");
                  if (isAxiosError(e)) {
                    SendToTrackingService(e);
                  }
                }
              },
              (errors, event) => {
                console.error(errors, event);
              }
            )();
          }}
          variant={ButtonVariantOption.Contained}
          size={SizingWrapperStyle.SMALL}
        />
      </Stack>
      <Snackbar
        open={uploadOpen}
        autoHideDuration={3000}
        onClose={() => {
          setUploadOpen(false);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => {
            setUploadOpen(false);
          }}
          severity="warning"
          sx={{ width: "100%" }}
        >
          {uploadMessage}
        </Alert>
      </Snackbar>
    </CustomModal>
  );
};

export default EditModal;
